import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import LicenceActions, {
   getLicenceData,
   isFetchingLicence,
} from 'pgdb-data-layer/lib/Redux/LicenceRedux';
import ApplicationsAction, { patchApplicationRequest, isFetchingApplicationSuccess, isSavingApplication } from 'pgdb-data-layer/lib/Redux/ApplicationsRedux';
import * as REGISTRATION from 'pgdb-data-layer/lib/Constants/Registration';
import Loading from '../../Components/Loading';
import StepIndicator from '../../Components/StepIndicator';
import { getToken } from '../../Session/SessionRedux';
import Navigator from '../../Navigation/Navigator';
import EndorsementSelectionStep from './Endorsements/EndorsementSelectionStep';
import './Endorsements/ApplyEndorsement.scss';
import EndorsementActions, { getEndorsement, isFetchingEndorsement } from 'pgdb-data-layer/lib/Redux/EndorsementRedux';
import { STEPS_ENDORSEMENT_FORM } from './EndorsementTypePage';
import EndorsementTimeLimitNotice from './Endorsements/EndorsementTimeLimitNotice';
import { ENDORSEMENT_TYPE_PLUMBER, ENDORSEMENT_TYPE_DRAINLAYER } from 'pgdb-data-layer/lib/Helpers/Endorsement';

const EditEndorsementTypePage = ({ token, licences, isLoading, getLicences, updatePatchSuccess, activeEndorsement, getApplicationPayload, patchEndorsementApplication, getUserEndorsement, applicationEndorsements, endorsement, isRenewal }) => {
   const { applicationId } = useParams();

   const currentApplication = applicationEndorsements.find(
      app => String(app.applicationId) === String(applicationId)
   );

   const [selectedEndorsements, setSelectedEndorsements] = useState({
      plumber: false,
      drainlayer: false,
   });
   const [isDisabled, setIsDisabled] = useState(true);
   const [isPatching, setIsPatching] = useState(false);

   useEffect(() => {
      if (token && (!licences.currentLicences || licences.currentLicences.length === 0)) {
         getLicences(token);
      }
      if (token) {
         getUserEndorsement(token);
      }
   }, [token, getLicences, getUserEndorsement]);

   useEffect(() => {
      // only navigate if patchApplication was triggered from within this page
      if(applicationId && isPatching && updatePatchSuccess){
         if (isRenewal) {
            Navigator.toRenewEndorsementDetails(applicationId);
         } else {
            Navigator.toEndorsementDetails(applicationId);
         }
      }
   }, [applicationId, isPatching, updatePatchSuccess]);

   useEffect(() => {
      if(token && applicationId ){
         getApplicationPayload(token, applicationId); //Get payload information        
      }
   }, [token, applicationId]);

   useEffect(() => {
      if (currentApplication && currentApplication.endorsementTypeCodes) {
         setSelectedEndorsements({
            plumber: currentApplication.endorsementTypeCodes.includes(ENDORSEMENT_TYPE_PLUMBER),
            drainlayer: currentApplication.endorsementTypeCodes.includes(ENDORSEMENT_TYPE_DRAINLAYER),
         });
      } else if (endorsement && endorsement.length > 0) {
         const appFromPayload = endorsement.find(app => String(app.applicationId) === String(applicationId));
         if (appFromPayload && appFromPayload.endorsementTypeCodes) {
            setSelectedEndorsements({
               plumber: appFromPayload.endorsementTypeCodes.includes(ENDORSEMENT_TYPE_PLUMBER),
               drainlayer: appFromPayload.endorsementTypeCodes.includes(ENDORSEMENT_TYPE_DRAINLAYER),
            });
         }
      }
   }, [currentApplication, endorsement, applicationId]);

   useEffect(() => {
      setIsDisabled(!selectedEndorsements.plumber && !selectedEndorsements.drainlayer);
   }, [selectedEndorsements]);

   const [eligibility, setEligibility] = useState({hasPlumber: false, hasDrainlayer: false});

   React.useEffect(() => {
      if (!licences || !licences.currentLicences) {
         setEligibility({ hasPlumber: false, hasDrainlayer: false });
         return;
      }

      const endorsements = activeEndorsement.endorsements || [];
      const activeCertifying = licences.currentLicences.filter(
         licence =>
            licence.status === 'Active' &&
            (licence.typeCode === REGISTRATION.CERTIFYING_PLUMBER_CODE || licence.typeCode === REGISTRATION.CERTIFYING_DRAINLAYER_CODE)
      );

      const canRenewPlumber = endorsements.some(e => e.endorsementTypeCode === ENDORSEMENT_TYPE_PLUMBER && e.canApplyForRenewal);
      const canRenewDrainlayer = endorsements.some(e => e.endorsementTypeCode === ENDORSEMENT_TYPE_DRAINLAYER && e.canApplyForRenewal);
      const canApplyPlumber = activeCertifying.some(l => l.typeCode === REGISTRATION.CERTIFYING_PLUMBER_CODE) && !endorsements.some(e => e.endorsementTypeCode === ENDORSEMENT_TYPE_PLUMBER);
      const canApplyDrainlayer = activeCertifying.some(l => l.typeCode === REGISTRATION.CERTIFYING_DRAINLAYER_CODE) && !endorsements.some(e => e.endorsementTypeCode === ENDORSEMENT_TYPE_DRAINLAYER);

      if (isRenewal) {
         setEligibility({
            hasPlumber: canRenewPlumber || canApplyPlumber,
            hasDrainlayer: canRenewDrainlayer || canApplyDrainlayer,
            isRenewingPlumber: canRenewPlumber,
            isRenewingDrainlayer: canRenewDrainlayer,
         });
         return;
      }

      setEligibility({
         hasPlumber: canApplyPlumber,
         hasDrainlayer: canApplyDrainlayer,
      });
   }
   , [licences, activeEndorsement]);
   

   const handleNext = () => {
      setIsPatching(true);
      patchEndorsementApplication(token, applicationId, {
         endorsementTypeCodes: [
            ...(selectedEndorsements.plumber ? [ENDORSEMENT_TYPE_PLUMBER] : []),
            ...(selectedEndorsements.drainlayer ? [ENDORSEMENT_TYPE_DRAINLAYER] : []),
         ]
      });
   };

   return (
      <main>
         <Loading isLoading={isLoading} overlayOnChildren={true}>
            <div className="section no-pad-bot" id="index-banner">
               <div className="container registration-container">
                  <div className="row no-margin flex">
                     <div className="col s10 m10 l10 offset-l1">
                        <div className="row profile-row">
                           <h4 className="columnheader">{isRenewal ? 'Endorsement Renewal' : 'Endorsement Application'}</h4>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
            <StepIndicator steps={STEPS_ENDORSEMENT_FORM} currentStep={1} />
            <EndorsementTimeLimitNotice />
            <EndorsementSelectionStep
               selectedEndorsements={selectedEndorsements}
               setSelectedEndorsements={setSelectedEndorsements}
               eligibility={eligibility}
               endorsements={activeEndorsement.endorsements}
               isDisabled={isDisabled}
               onNext={handleNext}
               isEditMode={true}
               applicationId={applicationId}
               isRenewal={isRenewal}
            />
         </Loading>
      </main>
   );
};

EditEndorsementTypePage.propTypes = {
   token: PropTypes.string.isRequired,
   licences: PropTypes.object.isRequired,
   isLoading: PropTypes.bool.isRequired,
   getLicences: PropTypes.func.isRequired,
   getUserEndorsement: PropTypes.func.isRequired,
   activeEndorsement: PropTypes.object.isRequired,
   applicationEndorsements: PropTypes.array.isRequired,
   endorsement: PropTypes.array.isRequired,
   patchEndorsementApplication: PropTypes.func.isRequired,
   getApplicationPayload: PropTypes.func.isRequired,
   updatePatchSuccess: PropTypes.bool.isRequired,
   isRenewal: PropTypes.bool,
};

const mapStateToProps = state => ({
   isLoading: isFetchingLicence(state) || isSavingApplication('patchApplication', state) || isFetchingEndorsement('getUserEndorsement', state),
   licences: getLicenceData(state),
   token: getToken(state),
   activeEndorsement: getEndorsement(state).endorsement,
   applicationEndorsements: getEndorsement(state).endorsement.applicationEndorsements,
   endorsement: state.application.payload.applications || [],
   updatePatchSuccess: isFetchingApplicationSuccess('patchApplication', state) || false,
      
});

const mapDispatchToProps = {
   getLicences: LicenceActions.licenceRequest,
   getUserEndorsement: EndorsementActions.getUserEndorsementRequest,
   patchEndorsementApplication: ApplicationsAction.patchApplicationRequest,
   getApplicationPayload: ApplicationsAction.getApplicationPayloadRequest,
};

export default connect(mapStateToProps, mapDispatchToProps)(EditEndorsementTypePage);
